import InPageMessage from '@liveauctioneers/caterwaul-components/lib/InPageMessage/InPageMessage';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { CreditCard } from '@liveauctioneers/caterwaul-components/lib/types/CreditCard';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import PaymentMethodSelection from '@/components/PaymentMethodSelection/PaymentMethodSelection';
import { PaymentProviders } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchBidderCreditCardsIfNeeded,
    updateBidderCreditCard,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import {
    getAllowedToAddCards,
    getCreditCardError,
    getLAPaymentCardsLoaded,
    laPaymentCardsForProviderSelector,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { openAddCreditCardModal } from '@/redux/modules/modal';

import PaysafeModal from './PaysafeModal';

const INPAGE_MESSAGE_LINKS = [
    <Link to="https://help.liveauctioneers.com/article/134-how-do-i-pay-for-an-item">
        <FormattedMessage id="learn_more" />
    </Link>,
    <Link to="mailto:info@liveauctioneers.com">
        <FormattedMessage id="contact_us" />
    </Link>,
];

const CardManagement = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const [cardAddError, setCardAddError] = useState<string>(undefined);
    const [cardUpdateError, setCardUpdateError] = useState<string>(undefined);
    const [showModal, setShowModal] = useState<boolean>(false);
    const allowedToAddCards = useAppSelector(getAllowedToAddCards);
    const laPaymentCardLoaded = useAppSelector(getLAPaymentCardsLoaded);
    const creditCardError = useAppSelector(getCreditCardError);
    const payrixCards: CreditCard[] = useAppSelector((state) =>
        laPaymentCardsForProviderSelector(state, PaymentProviders.Payrix)
    );
    // high risk can be paysafe and/or payrix.
    const highRiskCards: CreditCard[] = useAppSelector((state) =>
        laPaymentCardsForProviderSelector(state, PaymentProviders.Payrix)
    );
    useEffect(() => {
        if (!laPaymentCardLoaded) {
            dispatch(fetchBidderCreditCardsIfNeeded());
        }
    }, [dispatch, laPaymentCardLoaded]);

    useEffect(() => {
        if (
            creditCardError === "Can't remove card on file because there are unpaid items" ||
            creditCardError === "Can't remove card on file because there are pending absentee bids"
        ) {
            setCardUpdateError(formatMessage({ id: 'accountSettings.cannotRemoveCreditCard' }));
        } else if (creditCardError === "Can't remove card on file because there are unpaid items or open disputes") {
            setCardUpdateError(
                formatMessage({
                    id: 'accountSettings.cannotRemoveCreditCardOpenDisputes',
                })
            );
        }
    }, [creditCardError, formatMessage]);

    const onCardAdd = (provider: PaymentProviders) => {
        if (allowedToAddCards) {
            setCardAddError(undefined);
            dispatch(openAddCreditCardModal(provider));
        } else {
            setCardAddError(formatMessage({ id: 'creditCards.notAllowedToAddCards' }));
        }
    };

    const onPayrixCardAdd = () => {
        onCardAdd(PaymentProviders.Payrix);
    };

    const onDeleteCard = (cardId: number) => {
        dispatch(
            updateBidderCreditCard({
                cardId,
                defaultCard: false,
                enabled: false,
            })
        );
    };

    const onEditCard = (card: CreditCard) => {
        dispatch(
            updateBidderCreditCard({
                cardId: card.id,
                defaultCard: true,
                enabled: true,
            })
        );
    };

    const openModalText = (
        <Link onClick={() => setShowModal(true)}>
            <FormattedMessage id="accountSettings.openModal" />
        </Link>
    );

    return (
        <>
            <StyledPaymentText h2>
                <FormattedMessage id="accountSettings.payments" />
            </StyledPaymentText>
            {cardUpdateError && (
                <InPageMessage
                    links={INPAGE_MESSAGE_LINKS}
                    onDismiss={() => {
                        setCardUpdateError(undefined);
                    }}
                    type="error"
                >
                    {cardUpdateError}
                </InPageMessage>
            )}
            {payrixCards?.length > 0 ? (
                <PaymentMethodSelection
                    creditCards={payrixCards}
                    deleteCard={onDeleteCard}
                    selectCard={onEditCard}
                />
            ) : (
                <StyledNoCardsText secondary>
                    <FormattedMessage id="accountSettings.noCardOnFile" />
                </StyledNoCardsText>
            )}
            {highRiskCards?.length > 0 ? (
                <PaymentMethodSelection
                    creditCards={highRiskCards}
                    deleteCard={onDeleteCard}
                    selectCard={onEditCard}
                    title={
                        <FormattedMessage
                            id="accountSettings.highRiskCofLabel"
                            values={{ openModal: openModalText }}
                        />
                    }
                />
            ) : null}
            <PaysafeModal
                setShowModal={setShowModal}
                showModal={showModal}
            />

            <StyledAddCardButton
                data-testid="accountSettingsAddCardButton"
                onClick={onPayrixCardAdd}
            >
                {formatMessage({ id: 'accountSettings.addCardOnFileButton' })}
            </StyledAddCardButton>

            {cardAddError && <Text colorError>{cardAddError}</Text>}
        </>
    );
};

export default CardManagement;

const StyledPaymentText = styled(Text)`
    margin: 0;
`;

const StyledNoCardsText = styled(Text)`
    margin: 20px 0;
`;

const StyledAddCardButton = styled(Button)`
    width: 194px;
    margin: 32px 10px 16px 0;

    @media (max-width: 500px) {
        width: 100%;
    }
`;
